import { Link } from "react-router-dom";
import { Calendar } from "lucide-react";
import { optimizeImage, cloudinarySrcSet, cloudinaryTypedSrcSet } from "../utils/optimizeImage";

// Card thumbnails: AVIF → WebP → f_auto (this Cloudinary account's f_auto often returns JPEG), and phones are capped
// at the 800 px candidate (≈2× a full-width card) instead of being pushed to 1200/1600 by high-DPR screens.
const CARD_WIDTHS = [400, 800, 1200, 1600];
const CARD_WIDTHS_MOBILE = [400, 800];
const CardPicture = ({ image, sizes, alt }) => {
  const img = (
    <img
      src={optimizeImage(image, { width: 800 })}
      srcSet={cloudinarySrcSet(image, CARD_WIDTHS)}
      sizes={sizes}
      width="1200"
      height="800"
      alt={alt}
      loading="lazy"
      decoding="async"
      className="editorial-card-image"
    />
  );
  const avif = cloudinaryTypedSrcSet(image, "avif", CARD_WIDTHS);
  if (!avif) return img;
  return (
    <picture>
      <source type="image/avif" media="(max-width: 767px)" srcSet={cloudinaryTypedSrcSet(image, "avif", CARD_WIDTHS_MOBILE)} sizes="100vw" />
      <source type="image/avif" srcSet={avif} sizes={sizes} />
      <source type="image/webp" media="(max-width: 767px)" srcSet={cloudinaryTypedSrcSet(image, "webp", CARD_WIDTHS_MOBILE)} sizes="100vw" />
      <source type="image/webp" srcSet={cloudinaryTypedSrcSet(image, "webp", CARD_WIDTHS)} sizes={sizes} />
      {img}
    </picture>
  );
};
import SelectMark from "./SelectMark";

const ArticleCard = ({ article, variant = "default", className = "", sizes = "(min-width: 1024px) 400px, (min-width: 768px) 50vw, 100vw" }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      people: "People",
      companies: "Companies",
      brands: "Brands",
      ideas: "Ideas",
      initiatives: "Initiatives",
      specials: "Specials",
      events: "Events",
      news: "News"
    };
    return labels[category] || category;
  };

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className={`editorial-card block relative overflow-hidden ${className}`}
        data-testid={`featured-article-${article.slug}`}
      >
        <div className="relative h-full min-h-[520px] md:min-h-[620px]">
          <img
            src={optimizeImage(article.image_url, { width: 1500 })}
            alt={article.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-16">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-5">
                <span className="editorial-overline-light">
                  {getCategoryLabel(article.category)}<SelectMark article={article} light />
                </span>
                {article.is_sponsored && (
                  <span className="sponsored-badge">Sponsored</span>
                )}
              </div>
              <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-black text-white mb-5 leading-[1.05] tracking-tight max-w-4xl">
                {article.title}
              </h1>
              <p className="text-white/70 text-base md:text-lg mb-6 line-clamp-2 max-w-2xl">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-4 text-white/50 text-sm">
                <span className="font-medium text-white/70">{article.author}</span>
                <span className="w-1 h-1 rounded-full bg-white/30"></span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(article.published_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className={`group flex gap-6 py-6 ${className}`}
        style={{ borderBottom: '1px solid rgba(10,10,10,0.06)' }}
        data-testid={`horizontal-article-${article.slug}`}
      >
        <div className="editorial-image-wrapper w-28 h-20 md:w-44 md:h-28 flex-shrink-0">
          <img
            src={optimizeImage(article.image_url, { width: 400 })}
            srcSet={cloudinarySrcSet(article.image_url, [200, 400, 600])}
            sizes="(min-width: 768px) 176px, 112px"
            width="600"
            height="400"
            alt={article.title}
            loading="lazy"
            decoding="async"
            className="editorial-card-image"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="editorial-overline text-[0.65rem] mb-2 block">
            {getCategoryLabel(article.category)}<SelectMark article={article} />
          </span>
          <h3 className="font-serif text-base md:text-lg font-semibold text-neutral-950 mb-2 line-clamp-2 group-hover:text-wine-700 transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 text-neutral-500 text-xs">
            <span>{article.author}</span>
            <span className="w-1 h-1 rounded-full bg-neutral-300"></span>
            <span>{formatDate(article.published_at)}</span>
          </div>
        </div>
      </Link>
    );
  }

  // Default card, bordered newspaper-style
  return (
    <Link
      to={`/article/${article.slug}`}
      className={`editorial-card group block grid-card-bordered ${className}`}
      data-testid={`article-card-${article.slug}`}
    >
      <div className="editorial-image-wrapper aspect-[3/2] mb-5">
        <CardPicture image={article.image_url} sizes={sizes} alt={article.title} />
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="editorial-overline text-[0.65rem]">
            {getCategoryLabel(article.category)}<SelectMark article={article} />
          </span>
          {article.is_sponsored && (
            <span className="sponsored-badge text-[0.6rem]">Sponsored</span>
          )}
        </div>
        <h3 className="font-serif text-xl font-semibold text-neutral-950 line-clamp-2 group-hover:text-wine-700 transition-colors leading-snug">
          {article.title}
        </h3>
        <p className="text-neutral-600 text-sm line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 text-neutral-500 text-xs pt-2">
          <span>{article.author}</span>
          <span className="w-1 h-1 rounded-full bg-neutral-300"></span>
          <span>{formatDate(article.published_at)}</span>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
